import { FC, useEffect, useRef, useState } from "react";
import { KTSVG } from "../../_metronic/helpers";
import moment from "moment";
import { Reimburse } from "../../interfaces/Reimbursement";
import { currencyFormatter, customThousandSeparatorDot, isColorDarkFromHex } from "../../functions/general";
import { ReimburseAttachments } from "./ReimburseAttachements";
import { useIntl } from "react-intl";
import { useSettingsStore } from "../../stores/SettingsStore";
import useUser from "../../hooks/User";
import useModalEffect from "../../hooks/useModalEffect";
import { useReimbursementStore } from "../../stores/ReimbursementStore";
import { usePipelineStore } from "../../stores/PipelineStore";
import { returnCustomFieldMeeting, returnCustomFieldMerchant, returnCustomFieldPipeline, returnExpenseNominal, returnFile, returnImage, returnInvoiceReminder, returnLink, returnMultiple, returnOption, returnParagraph, returnReference, returnReimbursementCategory } from "../General/DetailMasterElement";
import { CustomField } from "../../interfaces/Settings";
import { Label } from "../../interfaces/Label";

interface Props {
    data: Reimburse
    setShowDetailMeetingModal: React.Dispatch<React.SetStateAction<boolean>>
    setShowDetailMerchantModal: React.Dispatch<React.SetStateAction<boolean>>
}

interface ItemTypeData {
    name: string
    value: string | JSX.Element | number | Label[]
}

interface CustomData {
    [key: string]: ItemTypeData
}

const DetailReimbursement: FC<Props> = ({ data, setShowDetailMeetingModal, setShowDetailMerchantModal }) => {
    const closeModalRef = useRef<HTMLButtonElement>(null);
    const [showAttachmentModal, setShowAttachmentModal] = useState<boolean>(false);
    const [fileAttachment, setFileAttachment] = useState<string>('');
    const [reimburse, setReimburse] = useState<Reimburse>();
    const [reimburseType, setReimburseType] = useState<string>('');
    const [customReimbursement, setCustomReimbursement] = useState({} as any)
    const [customFields, setCustomFields] = useState<CustomField>({})
    const intl = useIntl()
    const user = useUser()
    const { settings } = useSettingsStore()
    const { simplePipelines } = usePipelineStore()
    const { selectMultiReimbursement } = useReimbursementStore()

    const checkStatus = (status: string) => {
        switch (status) {
            case 'REJECTED':
                return <span className="badge badge-light-danger">{intl.formatMessage({ id: 'REIMBURSEMENT.STATUS.REJECTED' })}</span>
            case 'APPROVED':
                return <span className="badge badge-light-success">{intl.formatMessage({ id: 'REIMBURSEMENT.STATUS.APPROVED' })}</span>
            case 'PAID_OFF':
                return <span className="badge badge-light-primary">{intl.formatMessage({ id: 'REIMBURSEMENT.STATUS.PAID_OFF' })}</span>
            default:
                return <span className="badge badge-light-warning">{intl.formatMessage({ id: 'REIMBURSEMENT.STATUS.PENDING' })}</span>
        }
    }

    const handleClickDetail = (type: string) => {
        closeModalRef.current?.click()
        if (type === 'MEETING') setShowDetailMeetingModal(true)
        else if (type === 'MERCHANT') setShowDetailMerchantModal(true)
    }

    useEffect(() => {
        const customData: CustomData = {
            name: {
                name: intl.formatMessage({ id: 'FORM.LABEL.NAME' }),
                value: data.name
            },
            reimbursementCategory: {
                name: intl.formatMessage({ id: 'FORM.LABEL.CATEGORY' }),
                value: returnReimbursementCategory(data)
            },
            approvalStatus: {
                name: intl.formatMessage({ id: 'FORM.LABEL.STATUS' }),
                value: checkStatus(data.approvalStatus)
            },
            value: {
                name: intl.formatMessage({ id: 'FORM.LABEL.EXPENSE_NOMINAL' }),
                value: returnExpenseNominal(data)
            },
            description: {
                name: intl.formatMessage({ id: 'FORM.LABEL.DESCRIPTION' }),
                value: data.description
            },
            dateCreated: {
                name: intl.formatMessage({ id: 'FORM.LABEL.DATE_SUBMITTED' }),
                value: moment(data.dateCreated).format('DD MMMM YYYY')
            },
            dateSpend: {
                name: intl.formatMessage({ id: 'FORM.LABEL.EXPENSE_DATE' }),
                value: moment(data.dateSpend).format('DD MMMM YYYY')
            },
            dateApproved: {
                name: intl.formatMessage({ id: 'FORM.LABEL.DATE_APPROVED' }),
                value: data.dateApproved ? moment(data.dateApproved).format('DD MMMM YYYY') : "-"
            },
            dateRejected: {
                name: intl.formatMessage({ id: 'FORM.LABEL.DATE_REJECTED' }),
                value: data.dateRejected ? moment(data.dateRejected).format('DD MMMM YYYY') : "-"
            },
            rejectedReason: {
                name: intl.formatMessage({ id: 'FORM.LABEL.REASON_REJECTED' }),
                value: data.rejectedReason ? data.rejectedReason : "-"
            },
            datePaidOff: {
                name: intl.formatMessage({ id: 'FORM.LABEL.DATE_PAID_OFF' }),
                value: data.datePaidOff ? moment(data.datePaidOff).format('DD MMMM YYYY') : "-"
            },
            reference: {
                name: data.reference.type === 'MEETING' ? settings.meeting_title : settings.merchant_title,
                value: data.reference.id !== null ? returnReference(data, handleClickDetail) : "-"
            },
        }

        var newData = {
            ...Object.keys(customFields).reduce((acc, key) => {
                let value;
                const dataCustomFields = data.customFields[key]
                const dataInfoCustomFields = (data as any)?.info_custom_fields?.[key];

                if (customFields[key].type === 'invoice_reminder' && dataCustomFields) {
                    if (JSON.parse(dataCustomFields as string).invoice_url && JSON.parse(dataCustomFields as string).date_due) {
                        value = returnInvoiceReminder(JSON.parse(dataCustomFields as string).invoice_url, JSON.parse(dataCustomFields as string).date_due, intl)
                    } else if (JSON.parse(dataCustomFields as string).invoice_url && !JSON.parse(dataCustomFields as string).date_due) {
                        value = <a href={JSON.parse(dataCustomFields as string).invoice_url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_INVOICE" })}</a>
                    } else if (!JSON.parse(dataCustomFields as string).invoice_url && JSON.parse(dataCustomFields as string).date_due) {
                        value = moment(JSON.parse(dataCustomFields as string).date_due).format("DD MMM YYYY")
                    } else value = "-"
                }
                else if (customFields[key].type === 'date') {
                    value = dataCustomFields ? moment(dataCustomFields).format("DD MMM YYYY") : "-";
                } else if (customFields[key].type === 'image' || customFields[key].type === 'images') {
                    value = dataCustomFields ? returnImage(dataCustomFields, intl) : "-";
                } else if (customFields[key].type === 'file' || customFields[key].type === 'files') {
                    value = dataCustomFields ? returnFile(dataCustomFields, intl) : "-";
                } else if (customFields[key].type === 'multiple' || customFields[key].type === 'multipleOption') {
                    value = dataCustomFields ? returnMultiple(dataCustomFields as string) : "-";
                } else if (customFields[key].type === 'link') {
                    value = dataCustomFields ? returnLink(dataCustomFields as string) : "-";
                } else if (customFields[key].type === 'serial') {
                    value = typeof dataCustomFields === 'string' ? dataCustomFields : (dataCustomFields as string[])?.length > 0 ? (dataCustomFields as string[]).join('') : '-';
                } else if (customFields[key].type === 'merchant') {
                    value = dataInfoCustomFields && dataInfoCustomFields.length > 0 ? dataInfoCustomFields.map((item: any, index: number) => (
                        <>
                            {index > 0 && ', '}
                            {returnCustomFieldMerchant(item)}
                        </>
                    )) : "-";
                } else if (customFields[key].type === 'user') {
                    value = dataInfoCustomFields ? dataInfoCustomFields.map((item: any) => item.name).join(', ') : "-";
                } else if (customFields[key].type === 'dateTimeRange') {
                    value = dataCustomFields ? (dataCustomFields as string[]).map((item: any) => moment(item).format("DD MMM YYYY HH:mm")).join(' - ') : '-';
                } else if (customFields[key].type === 'pipeline') {
                    let result: any = simplePipelines.find((item: any) => item.id === (dataInfoCustomFields?.[0] as any)?.id)

                    if (result) {
                        let name = result.name + " / " + currencyFormatter(result.nominal, user?.data?.companyCurrencyNeedRound, user?.data?.companyCurrency, { showPrefix: true }) + " / " + moment(result.dateCreated).format('DD MMM YYYY')
                        let idTemplate = (simplePipelines as any).find((item: any) => item.id === result.id)?.leadTemplateId
                        let idPipeline = result.id
                        value = returnCustomFieldPipeline(idTemplate!, idPipeline, name);
                    } else value = "-"
                } else if (customFields[key].type === 'meeting') {
                    value = dataInfoCustomFields && dataInfoCustomFields.length > 0 ? dataInfoCustomFields.map((item: any, index: number) => (
                        <>
                            {index > 0 && ', '}
                            {returnCustomFieldMeeting(item)}
                        </>
                    )) : "-";
                } else if (customFields[key].type === 'time') {
                    value = dataCustomFields ? moment(dataCustomFields).format('HH:mm') : '-'
                } else if (customFields[key].type === 'dateTime') {
                    value = dataCustomFields ? moment(dataCustomFields).format('DD MMM YYYY HH:mm') : '-'
                } else if (customFields[key].type === 'number') {
                    value = dataCustomFields ? customThousandSeparatorDot(dataCustomFields as unknown as number) : "-";
                } else if (customFields[key].type === 'option') {
                    value = dataCustomFields ? returnOption(dataCustomFields as string) : "-";
                } else if (customFields[key].type === 'paragraph') {
                    value = dataCustomFields ? returnParagraph(dataCustomFields as string) : "-";
                } else if (customFields[key].type === 'product') {
                    value = dataInfoCustomFields ? dataInfoCustomFields.map((item: any) => item.product_name).join(', ') : "-";
                } else {
                    value = dataCustomFields ? dataCustomFields : "-";
                }

                return {
                    ...acc,
                    [key]: {
                        name: customFields[key].name,
                        value
                    }
                };
            }, {})
        };

        setCustomReimbursement(newData);
    }, [customFields])

    useEffect(() => {
        if (Object.keys(selectMultiReimbursement).length !== 0) {
            setCustomFields(selectMultiReimbursement.customFields)
        }
    }, [selectMultiReimbursement])

    useModalEffect({
        showModal: showAttachmentModal,
        setShowModal: setShowAttachmentModal,
        modalId: `attachments-reimburse-modal`
    })

    return (
        <>
            <div className="modal fade" tabIndex={-1} id={`detail-reimbursement-modal-${data.id}`}>
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Detail {data.name}</h5>
                            <div
                                className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            >
                                <KTSVG
                                    path="/media/icons/duotune/arrows/arr061.svg"
                                    className="svg-icon svg-icon-2x"
                                />
                            </div>
                        </div>
                        <div className="modal-body">
                            <div className="row">
                                <div className="col-12 col-md-6">
                                    <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.NAME' })}</label>
                                    <div className="input-group mb-4">{data.name}</div>
                                    <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.CATEGORY' })}</label>
                                    <div className="input-group mb-4">
                                        {
                                            data.reimbursementCategory.color ?
                                                <span className="badge" style={{ backgroundColor: data.reimbursementCategory.color, color: isColorDarkFromHex(data.reimbursementCategory.color) ? "white" : "black" }}>{data.reimbursementCategory.name}</span>
                                                :
                                                <span className="badge badge-light-info" >{data.reimbursementCategory.name}</span>
                                        }
                                    </div>
                                    <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.STATUS' })}</label>
                                    <div className="input-group mb-4">{checkStatus(data.approvalStatus)}</div>
                                    {
                                        (user.data.is_superadmin || user.data.canApproveReimbursement || user.data.canUploadReimbursementProofOfPayment) &&
                                        <>
                                            <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.USER' })}</label>
                                            <div className="input-group mb-4">{data.userOwner.name}</div>
                                        </>
                                    }
                                    <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.EXPENSE_NOMINAL' })}</label>
                                    {
                                        data.companyCurrency.code === "USD" ?
                                            <div className="input-group mb-4">{currencyFormatter(data.value, false, "USD", { showPrefix: true })}</div>
                                            :
                                            <div className="input-group mb-4">{currencyFormatter(data.value, true, "IDR", { showPrefix: true })}</div>
                                    }
                                    <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.DESCRIPTION' })}</label>
                                    <div className="input-group mb-4">{data.description}</div>
                                    <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.DATE_SUBMITTED' })}</label>
                                    <div className="input-group mb-4">{moment(data.dateCreated).format('DD MMMM YYYY')}</div>
                                    <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.EXPENSE_DATE' })}</label>
                                    <div className="input-group mb-4">{moment(data.dateSpend).format('DD MMMM YYYY')}</div>
                                    {data.dateApproved &&
                                        <>
                                            <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.DATE_APPROVED' })}</label>
                                            <div className="input-group mb-4">{moment(data.dateApproved).format('DD MMMM YYYY')}</div>
                                        </>
                                    }
                                    {data.dateRejected &&
                                        <>
                                            <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.DATE_REJECTED' })}</label>
                                            <div className="input-group mb-4">{moment(data.dateRejected).format('DD MMMM YYYY')}</div>
                                        </>
                                    }
                                    {data.rejectedReason &&
                                        <>
                                            <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.REASON_REJECTED' })}</label>
                                            <div className="input-group mb-4">{data.rejectedReason}</div>
                                        </>
                                    }
                                    {data.datePaidOff &&
                                        <>
                                            <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.DATE_PAID_OFF' })}</label>
                                            <div className="input-group mb-4">{moment(data.datePaidOff).format('DD MMMM YYYY')}</div>
                                        </>
                                    }
                                    {
                                        data.reference.id !== null &&
                                        <>
                                            <label className='form-label fs-6 fw-bolder text-dark'>{data.reference.type === 'MEETING' ? settings.meeting_title : settings.merchant_title}</label>
                                            <div className="cursor-pointer input-group mb-4 d-flex align-items-center gap-1 text-primary" onClick={() => handleClickDetail(data.reference.type)}>
                                                {data.reference.type === 'MEETING' ? data.reference.meeting.merchant_name + ' - ' + moment(data.reference.meeting.date_meet).format('DD MMMM YYYY') : data.reference.merchant.name}
                                                <KTSVG path="/media/icons/duotune/arrows/arr095.svg" className='svg-icon-5 svg-icon-primary' />
                                            </div>
                                        </>
                                    }
                                </div>
                                <div className="col-12 col-md-6">
                                    {data.attachmentsUrl && data.attachmentsUrl.length > 0 && (
                                        <>
                                            <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.PROOF_OF_EXPENSE' })}</label>
                                            <div className="d-flex flex-wrap gap-5 mb-4">
                                                {data.attachmentsUrl.map((file: string) => {
                                                    return (
                                                        <div className="">
                                                            <img src={file} className="rounded border border-gray-400" alt="bukti" style={{ width: '100px', height: '100px', objectFit: 'cover', cursor: 'pointer' }} onClick={
                                                                () => {
                                                                    setShowAttachmentModal(true);
                                                                    setFileAttachment(file);
                                                                    setReimburse(data);
                                                                    setReimburseType('Pengeluaran');
                                                                }
                                                            } />
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </>
                                    )}
                                    {data.paidOffAttachmentUrls && data.paidOffAttachmentUrls.length > 0 && (
                                        <>
                                            <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.PROOF_OF_PAYMENT' })}</label>
                                            <div className="d-flex flex-wrap gap-5 mb-4">
                                                {data.paidOffAttachmentUrls.map((file: string) => {
                                                    return (
                                                        <div className="">
                                                            <img src={file} className="rounded border border-gray-400" alt="bukti" style={{ width: '100px', height: '100px', objectFit: 'cover', cursor: 'pointer' }} onClick={
                                                                () => {
                                                                    setShowAttachmentModal(true);
                                                                    setFileAttachment(file);
                                                                    setReimburse(data);
                                                                    setReimburseType('Pelunasan');
                                                                }
                                                            } />
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="row">
                                {
                                    Object.keys(customReimbursement).map((key, index) => {
                                        if (index === 0) {
                                            return (
                                                <>
                                                    <div className="separator separator-dashed my-6"></div>
                                                    <div className="row pb-2 col-12 col-lg-6 col-xl-6">
                                                        <div className="col-3 text-end" style={{ fontWeight: "500", color: "#767373" }}>{customReimbursement[key].name}</div>
                                                        <div className="col-9" style={{ fontWeight: "500", color: "black" }}>{customReimbursement[key].value}</div>
                                                    </div>
                                                </>
                                            )
                                        } else {
                                            return (
                                                <div className="row pb-2 col-12 col-lg-6 col-xl-6">
                                                    <div className="col-3 text-end" style={{ fontWeight: "500", color: "#767373" }}>{customReimbursement[key].name}</div>
                                                    <div className="col-9" style={{ fontWeight: "500", color: "black" }}>{customReimbursement[key].value}</div>
                                                </div>
                                            )
                                        }
                                    })
                                }

                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-light"
                                data-bs-dismiss="modal"
                                ref={closeModalRef}
                            >
                                {intl.formatMessage({ id: 'FORM.ACTION.CLOSE' })}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {
                showAttachmentModal && <ReimburseAttachments reimburse={reimburse!} image={fileAttachment!} reimburseType={reimburseType!} />
            }
        </>
    )
}

export { DetailReimbursement }